/**
 * Generato automaticamente.
 * framework-cfg-procedimenti
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type {
  AnagraficaFasiDTO,
  GruppoAllegatoDTO,
  SezioneDTO,
  TipoComunicazioneDTO,
  TipoDocumentoDto,
  TipoPagamentoDTO,
  TipoProcedimentoDTO,
  TipoSezioneDocumentoDto,
  UserTaskRequest,
  UserTaskResponse,
} from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Restituisce il dettaglio  della configurazione di un task comprensiva degli oggetti collegati. Per ogni oggetto collegato restituisce l’elenco delle azioni possibili, e di queste indica quali sono azioni obbligatorie rispetto ad un esito del task.
 * @summary Restituisce il dettaglio  della configurazione di un task comprensiva degli oggetti collegati. Per ogni oggetto collegato restituisce l’elenco delle azioni possibili, e di queste indica quali sono azioni obbligatorie rispetto ad un esito del task.
 */
export const getTaskId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<UserTaskResponse>(
    { url: `/framework-cfg-procedimenti/task/${id}`, method: 'get', signal },
    options,
  );
};

export const getGetTaskIdQueryKey = (id: string) =>
  [`/framework-cfg-procedimenti/task/${id}`] as const;

export const getGetTaskIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getTaskId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTaskId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<Awaited<ReturnType<typeof getTaskId>>, TError, TData> & {
  queryKey: QueryKey;
} => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetTaskIdQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getTaskId>>> = ({
    signal,
  }) => getTaskId(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetTaskIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTaskId>>
>;
export type GetTaskIdQueryError = ErrorType<unknown>;

/**
 * @summary Restituisce il dettaglio  della configurazione di un task comprensiva degli oggetti collegati. Per ogni oggetto collegato restituisce l’elenco delle azioni possibili, e di queste indica quali sono azioni obbligatorie rispetto ad un esito del task.
 */
export const useGetTaskId = <
  TData = Awaited<ReturnType<typeof getTaskId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTaskId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetTaskIdQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Salva in modalità massiva, l'intera configurazione del task passando in input l'elenco degli oggetti collegati. Per ogni oggetto collegato trasmette l'elenco delle azioni possibili e di queste indica quali sono azioni obbligatorie rispetto ad un esito del task
 * @summary Salva in modalità massiva, l'intera configurazione del task passando in input l'elenco degli oggetti collegati. Per ogni oggetto collegato trasmette l'elenco delle azioni possibili e di queste indica quali sono azioni obbligatorie rispetto ad un esito del task
 */
export const putTaskId = (
  id: string,
  userTaskRequest: UserTaskRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<UserTaskResponse>(
    {
      url: `/framework-cfg-procedimenti/task/${id}`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: userTaskRequest,
    },
    options,
  );
};

export const getPutTaskIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putTaskId>>,
    TError,
    { id: string; data: UserTaskRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putTaskId>>,
  TError,
  { id: string; data: UserTaskRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putTaskId>>,
    { id: string; data: UserTaskRequest }
  > = props => {
    const { id, data } = props ?? {};

    return putTaskId(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutTaskIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof putTaskId>>
>;
export type PutTaskIdMutationBody = UserTaskRequest;
export type PutTaskIdMutationError = ErrorType<unknown>;

/**
 * @summary Salva in modalità massiva, l'intera configurazione del task passando in input l'elenco degli oggetti collegati. Per ogni oggetto collegato trasmette l'elenco delle azioni possibili e di queste indica quali sono azioni obbligatorie rispetto ad un esito del task
 */
export const usePutTaskId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putTaskId>>,
    TError,
    { id: string; data: UserTaskRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPutTaskIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Restituisce il tipo pagamento associato ad un task
 * @summary Tipo pagamento associato ad un task
 */
export const getTaskIdTipoPagamento = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<TipoPagamentoDTO>(
    {
      url: `/framework-cfg-procedimenti/task/${id}/tipo-pagamento`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetTaskIdTipoPagamentoQueryKey = (id: string) =>
  [`/framework-cfg-procedimenti/task/${id}/tipo-pagamento`] as const;

export const getGetTaskIdTipoPagamentoQueryOptions = <
  TData = Awaited<ReturnType<typeof getTaskIdTipoPagamento>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTaskIdTipoPagamento>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getTaskIdTipoPagamento>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetTaskIdTipoPagamentoQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getTaskIdTipoPagamento>>
  > = ({ signal }) => getTaskIdTipoPagamento(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetTaskIdTipoPagamentoQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTaskIdTipoPagamento>>
>;
export type GetTaskIdTipoPagamentoQueryError = ErrorType<unknown>;

/**
 * @summary Tipo pagamento associato ad un task
 */
export const useGetTaskIdTipoPagamento = <
  TData = Awaited<ReturnType<typeof getTaskIdTipoPagamento>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTaskIdTipoPagamento>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetTaskIdTipoPagamentoQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Restituisce l’elenco delle sezioni associate ad un task.
 * @summary Lista tipi sezioni
 */
export const getTaskIdTipiSezioni = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<TipoSezioneDocumentoDto[]>(
    {
      url: `/framework-cfg-procedimenti/task/${id}/tipi-sezioni`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetTaskIdTipiSezioniQueryKey = (id: string) =>
  [`/framework-cfg-procedimenti/task/${id}/tipi-sezioni`] as const;

export const getGetTaskIdTipiSezioniQueryOptions = <
  TData = Awaited<ReturnType<typeof getTaskIdTipiSezioni>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTaskIdTipiSezioni>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getTaskIdTipiSezioni>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetTaskIdTipiSezioniQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getTaskIdTipiSezioni>>
  > = ({ signal }) => getTaskIdTipiSezioni(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetTaskIdTipiSezioniQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTaskIdTipiSezioni>>
>;
export type GetTaskIdTipiSezioniQueryError = ErrorType<unknown>;

/**
 * @summary Lista tipi sezioni
 */
export const useGetTaskIdTipiSezioni = <
  TData = Awaited<ReturnType<typeof getTaskIdTipiSezioni>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTaskIdTipiSezioni>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetTaskIdTipiSezioniQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Restituisce l’elenco dei tipi documento associati ad un task. Per ogni documento restituisce l’elenco delle sezioni
 * @summary Lista tipi documento
 */
export const getTaskIdTipiDocumenti = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<TipoDocumentoDto[]>(
    {
      url: `/framework-cfg-procedimenti/task/${id}/tipi-documenti`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetTaskIdTipiDocumentiQueryKey = (id: string) =>
  [`/framework-cfg-procedimenti/task/${id}/tipi-documenti`] as const;

export const getGetTaskIdTipiDocumentiQueryOptions = <
  TData = Awaited<ReturnType<typeof getTaskIdTipiDocumenti>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTaskIdTipiDocumenti>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getTaskIdTipiDocumenti>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetTaskIdTipiDocumentiQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getTaskIdTipiDocumenti>>
  > = ({ signal }) => getTaskIdTipiDocumenti(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetTaskIdTipiDocumentiQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTaskIdTipiDocumenti>>
>;
export type GetTaskIdTipiDocumentiQueryError = ErrorType<unknown>;

/**
 * @summary Lista tipi documento
 */
export const useGetTaskIdTipiDocumenti = <
  TData = Awaited<ReturnType<typeof getTaskIdTipiDocumenti>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTaskIdTipiDocumenti>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetTaskIdTipiDocumentiQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Restituisce l’elenco dei tipi di comunicazioni associati ad un task
 * @summary Lista tipi comunicazione
 */
export const getTaskIdTipiComunicazione = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<TipoComunicazioneDTO[]>(
    {
      url: `/framework-cfg-procedimenti/task/${id}/tipi-comunicazione`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetTaskIdTipiComunicazioneQueryKey = (id: string) =>
  [`/framework-cfg-procedimenti/task/${id}/tipi-comunicazione`] as const;

export const getGetTaskIdTipiComunicazioneQueryOptions = <
  TData = Awaited<ReturnType<typeof getTaskIdTipiComunicazione>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTaskIdTipiComunicazione>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getTaskIdTipiComunicazione>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetTaskIdTipiComunicazioneQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getTaskIdTipiComunicazione>>
  > = ({ signal }) => getTaskIdTipiComunicazione(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetTaskIdTipiComunicazioneQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTaskIdTipiComunicazione>>
>;
export type GetTaskIdTipiComunicazioneQueryError = ErrorType<unknown>;

/**
 * @summary Lista tipi comunicazione
 */
export const useGetTaskIdTipiComunicazione = <
  TData = Awaited<ReturnType<typeof getTaskIdTipiComunicazione>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTaskIdTipiComunicazione>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetTaskIdTipiComunicazioneQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Restituisce l’elenco delle sezioni dati associate ad un task.
 * @summary Lista sezioni dati
 */
export const getTaskIdSezioniDati = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<SezioneDTO[]>(
    {
      url: `/framework-cfg-procedimenti/task/${id}/sezioni-dati`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetTaskIdSezioniDatiQueryKey = (id: string) =>
  [`/framework-cfg-procedimenti/task/${id}/sezioni-dati`] as const;

export const getGetTaskIdSezioniDatiQueryOptions = <
  TData = Awaited<ReturnType<typeof getTaskIdSezioniDati>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTaskIdSezioniDati>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getTaskIdSezioniDati>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetTaskIdSezioniDatiQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getTaskIdSezioniDati>>
  > = ({ signal }) => getTaskIdSezioniDati(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetTaskIdSezioniDatiQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTaskIdSezioniDati>>
>;
export type GetTaskIdSezioniDatiQueryError = ErrorType<unknown>;

/**
 * @summary Lista sezioni dati
 */
export const useGetTaskIdSezioniDati = <
  TData = Awaited<ReturnType<typeof getTaskIdSezioniDati>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTaskIdSezioniDati>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetTaskIdSezioniDatiQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Restituisce l’elenco dei gruppi allegato associati ad un task.
 * @summary Lista tipi documento
 */
export const getTaskIdGruppoAllegati = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<GruppoAllegatoDTO[]>(
    {
      url: `/framework-cfg-procedimenti/task/${id}/gruppo-allegati`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetTaskIdGruppoAllegatiQueryKey = (id: string) =>
  [`/framework-cfg-procedimenti/task/${id}/gruppo-allegati`] as const;

export const getGetTaskIdGruppoAllegatiQueryOptions = <
  TData = Awaited<ReturnType<typeof getTaskIdGruppoAllegati>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTaskIdGruppoAllegati>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getTaskIdGruppoAllegati>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetTaskIdGruppoAllegatiQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getTaskIdGruppoAllegati>>
  > = ({ signal }) => getTaskIdGruppoAllegati(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetTaskIdGruppoAllegatiQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTaskIdGruppoAllegati>>
>;
export type GetTaskIdGruppoAllegatiQueryError = ErrorType<unknown>;

/**
 * @summary Lista tipi documento
 */
export const useGetTaskIdGruppoAllegati = <
  TData = Awaited<ReturnType<typeof getTaskIdGruppoAllegati>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTaskIdGruppoAllegati>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetTaskIdGruppoAllegatiQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Restituisce l’elenco degli endoprocedimenti associati ad un task
 * @summary Lista degli endoprocedimenti associati ad un task
 */
export const getTaskIdEndoProcedimenti = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<TipoProcedimentoDTO[]>(
    {
      url: `/framework-cfg-procedimenti/task/${id}/endo-procedimenti`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetTaskIdEndoProcedimentiQueryKey = (id: string) =>
  [`/framework-cfg-procedimenti/task/${id}/endo-procedimenti`] as const;

export const getGetTaskIdEndoProcedimentiQueryOptions = <
  TData = Awaited<ReturnType<typeof getTaskIdEndoProcedimenti>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTaskIdEndoProcedimenti>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getTaskIdEndoProcedimenti>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetTaskIdEndoProcedimentiQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getTaskIdEndoProcedimenti>>
  > = ({ signal }) => getTaskIdEndoProcedimenti(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetTaskIdEndoProcedimentiQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTaskIdEndoProcedimenti>>
>;
export type GetTaskIdEndoProcedimentiQueryError = ErrorType<unknown>;

/**
 * @summary Lista degli endoprocedimenti associati ad un task
 */
export const useGetTaskIdEndoProcedimenti = <
  TData = Awaited<ReturnType<typeof getTaskIdEndoProcedimenti>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTaskIdEndoProcedimenti>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetTaskIdEndoProcedimentiQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Restituisce la fase della configurazione di un task comprensiva degli oggetti collegati tramite id processo bpmn (process definition id) ed id task bpmn.
 * @summary Restituisce la fase di un task tramite id processo bpmn (process definition id) ed id task bpmn.
 */
export const getTaskIdProcessoBpmnIdTaskIdTaskBpmnFase = (
  idProcessoBpmn: string,
  idTaskBpmn: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<AnagraficaFasiDTO>(
    {
      url: `/framework-cfg-procedimenti/task/${idProcessoBpmn}/id-task/${idTaskBpmn}/fase`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetTaskIdProcessoBpmnIdTaskIdTaskBpmnFaseQueryKey = (
  idProcessoBpmn: string,
  idTaskBpmn: string,
) =>
  [
    `/framework-cfg-procedimenti/task/${idProcessoBpmn}/id-task/${idTaskBpmn}/fase`,
  ] as const;

export const getGetTaskIdProcessoBpmnIdTaskIdTaskBpmnFaseQueryOptions = <
  TData = Awaited<ReturnType<typeof getTaskIdProcessoBpmnIdTaskIdTaskBpmnFase>>,
  TError = ErrorType<unknown>,
>(
  idProcessoBpmn: string,
  idTaskBpmn: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTaskIdProcessoBpmnIdTaskIdTaskBpmnFase>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getTaskIdProcessoBpmnIdTaskIdTaskBpmnFase>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetTaskIdProcessoBpmnIdTaskIdTaskBpmnFaseQueryKey(
      idProcessoBpmn,
      idTaskBpmn,
    );

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getTaskIdProcessoBpmnIdTaskIdTaskBpmnFase>>
  > = ({ signal }) =>
    getTaskIdProcessoBpmnIdTaskIdTaskBpmnFase(
      idProcessoBpmn,
      idTaskBpmn,
      requestOptions,
      signal,
    );

  return {
    queryKey,
    queryFn,
    enabled: !!(idProcessoBpmn && idTaskBpmn),
    ...queryOptions,
  };
};

export type GetTaskIdProcessoBpmnIdTaskIdTaskBpmnFaseQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTaskIdProcessoBpmnIdTaskIdTaskBpmnFase>>
>;
export type GetTaskIdProcessoBpmnIdTaskIdTaskBpmnFaseQueryError =
  ErrorType<unknown>;

/**
 * @summary Restituisce la fase di un task tramite id processo bpmn (process definition id) ed id task bpmn.
 */
export const useGetTaskIdProcessoBpmnIdTaskIdTaskBpmnFase = <
  TData = Awaited<ReturnType<typeof getTaskIdProcessoBpmnIdTaskIdTaskBpmnFase>>,
  TError = ErrorType<unknown>,
>(
  idProcessoBpmn: string,
  idTaskBpmn: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTaskIdProcessoBpmnIdTaskIdTaskBpmnFase>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetTaskIdProcessoBpmnIdTaskIdTaskBpmnFaseQueryOptions(
    idProcessoBpmn,
    idTaskBpmn,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Restituisce il dettaglio  della configurazione di un task comprensiva degli oggetti collegati tramite id procedimento ed id task bpmn.
 * @summary Restituisce il dettaglio  della configurazione di un task comprensiva degli oggetti collegati tramite id procedimento ed id task bpmn.
 */
export const getTaskIdProcedimentoIdTaskIdTaskBpmn = (
  idProcedimento: string,
  idTaskBpmn: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<UserTaskResponse>(
    {
      url: `/framework-cfg-procedimenti/task/${idProcedimento}/id-task/${idTaskBpmn}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetTaskIdProcedimentoIdTaskIdTaskBpmnQueryKey = (
  idProcedimento: string,
  idTaskBpmn: string,
) =>
  [
    `/framework-cfg-procedimenti/task/${idProcedimento}/id-task/${idTaskBpmn}`,
  ] as const;

export const getGetTaskIdProcedimentoIdTaskIdTaskBpmnQueryOptions = <
  TData = Awaited<ReturnType<typeof getTaskIdProcedimentoIdTaskIdTaskBpmn>>,
  TError = ErrorType<unknown>,
>(
  idProcedimento: string,
  idTaskBpmn: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTaskIdProcedimentoIdTaskIdTaskBpmn>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getTaskIdProcedimentoIdTaskIdTaskBpmn>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetTaskIdProcedimentoIdTaskIdTaskBpmnQueryKey(
      idProcedimento,
      idTaskBpmn,
    );

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getTaskIdProcedimentoIdTaskIdTaskBpmn>>
  > = ({ signal }) =>
    getTaskIdProcedimentoIdTaskIdTaskBpmn(
      idProcedimento,
      idTaskBpmn,
      requestOptions,
      signal,
    );

  return {
    queryKey,
    queryFn,
    enabled: !!(idProcedimento && idTaskBpmn),
    ...queryOptions,
  };
};

export type GetTaskIdProcedimentoIdTaskIdTaskBpmnQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTaskIdProcedimentoIdTaskIdTaskBpmn>>
>;
export type GetTaskIdProcedimentoIdTaskIdTaskBpmnQueryError =
  ErrorType<unknown>;

/**
 * @summary Restituisce il dettaglio  della configurazione di un task comprensiva degli oggetti collegati tramite id procedimento ed id task bpmn.
 */
export const useGetTaskIdProcedimentoIdTaskIdTaskBpmn = <
  TData = Awaited<ReturnType<typeof getTaskIdProcedimentoIdTaskIdTaskBpmn>>,
  TError = ErrorType<unknown>,
>(
  idProcedimento: string,
  idTaskBpmn: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTaskIdProcedimentoIdTaskIdTaskBpmn>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetTaskIdProcedimentoIdTaskIdTaskBpmnQueryOptions(
    idProcedimento,
    idTaskBpmn,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
